import { useInfiniteQuery, useMutation, useQueries, useQueryClient } from '@tanstack/react-query';
import {
  FOLLOW_UPS_MODULE_PAGE_SIZE,
  FOLLOW_UP_ACTIVITY_LOGS_PAGE_SIZE,
  getConversationDetail,
  getFollowUpActivityLogs,
  getFollowUps,
  restoreFollowUp,
} from '../../../services/followUpService';
import { showError, showSuccess } from '../../../utils/toast';
import { getTabFilters } from '../../../components/followUps/utils/followUpsModuleUtils';
import { followUpActivityLogKeys, followUpsModuleKeys } from './followUpKeys';
import { markLocalFollowUpChange, removeFollowUpFromCaches, upsertFollowUpInCaches } from './followUpCache';

export { followUpActivityLogKeys, followUpsModuleKeys };

// Retry reads only on network/server failures
const shouldRetryQuery = (failureCount, error) => {
  const status = error?.response?.status;
  if (status && status < 500) return false;
  return failureCount < 2;
};

/**
 * One Follow-ups page tab (Due / Upcoming / Completed / Trash), cursor-paginated, 30 per page.
 * Page 1 loads once per tab; further pages only via `fetchNextPage` (on scroll). Realtime events
 * and mutations patch these caches directly, so there are no refetches after changes.
 *
 * The AbortSignal is deliberately not forwarded to axios: consuming it makes React Query cancel
 * and re-send the request on StrictMode's mount/unmount/remount, i.e. a duplicate GET.
 */
export const useFollowUpsModuleTab = (tab, { search = '', dayKey, enabled = true } = {}) =>
  useInfiniteQuery({
    queryKey: followUpsModuleKeys.list(tab, search, dayKey),
    queryFn: ({ pageParam }) =>
      getFollowUps({
        filters: getTabFilters(tab, new Date()),
        search,
        cursor: pageParam,
        limit: FOLLOW_UPS_MODULE_PAGE_SIZE,
      }),
    initialPageParam: null,
    getNextPageParam: (lastPage) => lastPage?.nextCursor ?? undefined,
    enabled,
    // Searching: keep this tab's current results on screen while the new search loads
    placeholderData: search
      ? (previousData, previousQuery) => (previousQuery?.queryKey?.[3] === tab ? previousData : undefined)
      : undefined,
    staleTime: 60 * 1000,
    // Dropped as soon as the Follow-ups page is left, so every visit loads fresh page-1 data
    // (and never re-requests previously scrolled pages). While the page is open, realtime events
    // and mutations keep these lists current without refetching.
    gcTime: 0,
    retry: shouldRetryQuery,
  });

/**
 * Reload the Follow-ups page lists (sidebar item clicked while already on the page): every tab
 * goes back to page 1 and refetches only that — scrolled pages aren't re-requested with cursors.
 */
export const refreshFollowUpsModule = (queryClient) => {
  queryClient.setQueriesData({ queryKey: followUpsModuleKeys.lists() }, (data) =>
    data?.pages?.length > 1 ? { pages: data.pages.slice(0, 1), pageParams: data.pageParams.slice(0, 1) } : data
  );
  return queryClient.invalidateQueries({ queryKey: followUpsModuleKeys.lists() });
};

/**
 * Reload one tab's list (the user opened that tab): page 1 only, scrolled pages aren't re-requested.
 * Only queries that are currently observed refetch.
 */
export const refreshFollowUpsModuleTab = (queryClient, tab) => {
  const queryKey = [...followUpsModuleKeys.lists(), tab, ''];
  queryClient.setQueriesData({ queryKey }, (data) =>
    data?.pages?.length > 1 ? { pages: data.pages.slice(0, 1), pageParams: data.pageParams.slice(0, 1) } : data
  );
  return queryClient.invalidateQueries({ queryKey, refetchType: 'active' });
};

/**
 * POST /api/chats/follow-ups/{id}/restore/ — moves the follow-up from Trash back to its tab.
 */
export const useRestoreFollowUp = () => {
  const queryClient = useQueryClient();
  return useMutation({
    retry: 0,
    mutationFn: ({ followUpId }) => restoreFollowUp(followUpId),
    onSuccess: ({ followUp, message }, { followUpId }) => {
      markLocalFollowUpChange(followUpId);
      upsertFollowUpInCaches(queryClient, followUp);
      showSuccess(message || 'Follow-up restored successfully');
    },
    onError: (error, { followUpId }) => {
      // Not found in this tenant any more — drop the stale card
      if (error?.response?.status === 404) removeFollowUpFromCaches(queryClient, followUpId);
      showError(error, 'Failed to restore follow-up. Please try again.');
    },
  });
};

/**
 * GET /api/chats/follow-ups/{id}/activity-logs/ — page 1 (20 rows) when the Activity Logs view
 * opens; page 2, 3, … only when the user scrolls and the previous page said `next`.
 * Dropped when the view closes, so reopening always shows fresh history.
 */
export const useFollowUpActivityLogs = (followUpId, { enabled = true } = {}) =>
  useInfiniteQuery({
    queryKey: followUpActivityLogKeys.byFollowUp(followUpId),
    queryFn: ({ pageParam }) =>
      getFollowUpActivityLogs(followUpId, { page: pageParam, pageSize: FOLLOW_UP_ACTIVITY_LOGS_PAGE_SIZE }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, _allPages, lastPageParam) => (lastPage?.hasNext ? lastPageParam + 1 : undefined),
    enabled: enabled && followUpId != null,
    staleTime: 30 * 1000,
    gcTime: 0,
    retry: shouldRetryQuery,
  });

export const followUpConversationKey = (conversationId) => ['followUpConversation', conversationId];

/**
 * Conversation summaries (name, channel, status) for follow-up cards whose conversation isn't in
 * the chat store yet. Each conversation is requested once and cached.
 * @returns {Object<number, object>} conversations by id
 */
export const useFollowUpConversations = (conversationIds, { enabled = true, onLoaded } = {}) => {
  const results = useQueries({
    queries: conversationIds.map((id) => ({
      queryKey: followUpConversationKey(id),
      queryFn: async () => {
        const detail = await getConversationDetail(id);
        onLoaded?.(detail);
        return detail;
      },
      enabled,
      staleTime: 10 * 60 * 1000,
      gcTime: 30 * 60 * 1000,
      retry: shouldRetryQuery,
    })),
  });
  return results.reduce((acc, result, index) => {
    if (result.data) acc[conversationIds[index]] = result.data;
    return acc;
  }, {});
};
